import React from "react";
import PropTypes from "prop-types";
import clsx from "clsx";
import DeleteIcon from "@material-ui/icons/Delete";
import useToolbarStyles from "./useToolbarStyles";
import { Link, Typography, IconButton, Toolbar, Tooltip, Chip, Grid } from "@material-ui/core";
import EditIcon from "@material-ui/icons/Edit";
import RemoveCircleIcon from "@material-ui/icons/RemoveCircle";
import { hasAttributes } from "utils/objects";
import SearchInput from "../SearchInput/SearchInput";

function EnhancedTableToolbar({
    selected,
    title,
    onDelete,
    onFilter,
    deletable,
    allowMultiSelect,
    filterable,
    renderBulkActions,
    clearSelection,
    advancedFiltersProps,
    filterValue,
    setFilterValue,
}) {
    const classes = useToolbarStyles();
    const numSelected = selected.length;

    const {
        withAdvancedFilters,
        advancedFilters,
        onResetAdvancedFilters,
        setAdvancedFiltersDialogProps,
        onResetSingleFilter,
        AdvancedFiltersNormalizers,
    } = advancedFiltersProps || {};

    const onQuickFilterChange = (e) => {
        setFilterValue(e.target.value);
    };

    const hasAdvancedFiltersApplied = hasAttributes(advancedFilters);

    function openAdvancedFilters() {
        setAdvancedFiltersDialogProps((prev) => ({ ...prev, open: true }));
    }

    const { keyNames, valueNormalizers } = AdvancedFiltersNormalizers || {};

    function getChipName(key) {
        return hasAttributes(keyNames) ? keyNames[key] : key;
    }

    function getChipValue(key, value) {
        return hasAttributes(valueNormalizers) && typeof valueNormalizers[key] === "function" ? valueNormalizers[key](value) : value;
    }

    return (
        <Toolbar
            className={clsx(classes.root, {
                [classes.highlight]: numSelected > 0 && allowMultiSelect,
            })}
        >
            <Grid container spacing={1}>
                <Grid item xs={12} md={6} className={clsx(classes.titleContainer)}>
                    {numSelected > 0 && allowMultiSelect ? (
                        <Typography color="inherit" variant="subtitle1">
                            {numSelected} selected
                        </Typography>
                    ) : (
                        <Typography variant="h6" id="tableTitle">
                            {title}
                        </Typography>
                    )}
                </Grid>
                <Grid item xs={12} md={6} className={clsx(classes.titleContainer)}>
                    {!(numSelected > 0 && allowMultiSelect) && filterable && !hasAdvancedFiltersApplied && (
                        <SearchInput onChange={onQuickFilterChange} value={filterValue} onSearch={onFilter} />
                    )}
                    {numSelected > 0 && deletable && (
                        <Tooltip title="Delete" onClick={onDelete}>
                            <IconButton aria-label="delete">
                                <DeleteIcon />
                            </IconButton>
                        </Tooltip>
                    )}
                    {numSelected > 0 && renderBulkActions(selected, clearSelection)}
                </Grid>
                {!(numSelected > 0 && allowMultiSelect) && (
                    <Grid item xs={12}>
                        {filterable && withAdvancedFilters && (
                            <>
                                {hasAdvancedFiltersApplied ? (
                                    <>
                                        <Tooltip title="Reset filters" onClick={onResetAdvancedFilters}>
                                            <IconButton aria-label="advanced-filters">
                                                <RemoveCircleIcon />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Advanced filters" onClick={openAdvancedFilters}>
                                            <IconButton aria-label="advanced-filters">
                                                <EditIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </>
                                ) : (
                                    <Link component="button" variant="body2" onClick={openAdvancedFilters}>
                                        Advanced filters
                                    </Link>
                                )}
                            </>
                        )}
                        {hasAdvancedFiltersApplied &&
                            Object.keys(advancedFilters).map((k) => (
                                <Chip
                                    key={`filter-chip-${k}`}
                                    label={`${getChipName(k)}: ${getChipValue(k, advancedFilters[k])}`}
                                    onDelete={() => onResetSingleFilter(k)}
                                    color="primary"
                                    className={clsx(classes.chip)}
                                />
                            ))}
                    </Grid>
                )}
            </Grid>
        </Toolbar>
    );
}

EnhancedTableToolbar.propTypes = {
    selected: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    title: PropTypes.string,
    onDelete: PropTypes.func,
    onFilter: PropTypes.func,
    deletable: PropTypes.bool,
    filterable: PropTypes.bool,
    allowMultiSelect: PropTypes.bool,
    renderBulkActions: PropTypes.func,
    clearSelection: PropTypes.func.isRequired,
    advancedFiltersProps: PropTypes.shape({
        advancedFilters: PropTypes.shape({}),
        onResetAdvancedFilters: PropTypes.func,
        setAdvancedFiltersDialogProps: PropTypes.func,
        withAdvancedFilters: PropTypes.bool,
    }),
};

EnhancedTableToolbar.defaultProps = {
    title: "",
    onDelete: () => {},
    onFilter: () => {},
    deletable: true,
    filterable: true,
    allowMultiSelect: true,
    renderBulkActions: () => {},
    advancedFiltersProps: {
        advancedFilters: undefined,
        setAdvancedFiltersDialogProps: () => {},
        onResetAdvancedFilters: () => {},
        withAdvancedFilters: false,
    },
};

export default EnhancedTableToolbar;
